import { createFileRoute } from "@tanstack/react-router";

// Inline zod-free tab whitelist. Same rationale as the parent route stub:
// keep zod out of the eager bundle. The lazy route may still use richer
// schemas internally; this validator only normalises `?tab=` for routing.
const TAB_WHITELIST = new Set([
  "cli",
  "session",
  "chat",
  "prompts",
  "artifacts",
  "sub-tasks",
  "subtasks",
  "notes",
  "history",
  "audit",
  "members",
  "role-messages",
  "rolemessages",
  "messages",
  "debate",
  "architect",
  "settings",
  "sub-agents",
  "calls",
  "research",
  // KB-Access P6 §6.2 — in-session delegation tab (gated by VITE_SCOPED_ACCESS_ENABLED).
  "access",
] as const);
type CardTab =
  | "cli"
  | "session"
  | "chat"
  | "prompts"
  | "artifacts"
  | "sub-tasks"
  | "subtasks"
  | "notes"
  | "history"
  | "audit"
  | "members"
  | "role-messages"
  | "rolemessages"
  | "messages"
  | "debate"
  | "architect"
  | "settings"
  | "sub-agents"
  | "calls"
  | "research"
  // KB-Access P6 §6.2 — in-session delegation tab.
  | "access";

function validateCardSearch(
  search: Record<string, unknown>,
): { tab?: CardTab; ask?: string; approval?: string; asid?: string; accessRequest?: string; artifact?: string } {
  const out: { tab?: CardTab; ask?: string; approval?: string; asid?: string; accessRequest?: string; artifact?: string } = {};
  const t = search?.tab;
  if (typeof t === "string" && TAB_WHITELIST.has(t as CardTab)) {
    out.tab = t as CardTab;
  }
  // `?ask=<askId>` deep-link (Conductor spec 04 §1.4 / §4.1): an Inbox
  // Needs-You "ask" row links here so the AskPopup auto-opens anchored in the
  // session view. Pass the raw id through (the popup fetch + org guard validate
  // it server-side); harmless when the popup is flag-off.
  const a = search?.ask;
  if (typeof a === "string" && a.length > 0) {
    out.ask = a;
  }
  // `?approval=<eventId>` (+ `?asid=<sessionId>`) deep-link (ADR-057): an Inbox
  // Needs-You "approval" row links here so the ApprovalPopup auto-opens anchored
  // in the session view. Pass the raw ids through (the popup fetch + org guard
  // validate them server-side); harmless when the popup is flag-off.
  const ap = search?.approval;
  if (typeof ap === "string" && ap.length > 0) {
    out.approval = ap;
  }
  const asid = search?.asid;
  if (typeof asid === "string" && asid.length > 0) {
    out.asid = asid;
  }
  // `?accessRequest=<id>` deep-link (migration 0104): an Inbox Needs-You
  // "access request" row links here so the AccessRequestPopup auto-opens
  // anchored in the session view. Pass the raw id through (the popup fetch +
  // org guard validate it server-side); harmless when the popup is flag-off.
  const areq = search?.accessRequest;
  if (typeof areq === "string" && areq.length > 0) {
    out.accessRequest = areq;
  }
  // `?artifact=<relPath>` deep-link (VITE_SESSION_ARTIFACTS_FOOTER): an
  // "Artifacts touched this run" footer link under an agent message opens the
  // card's Artifacts tab focused on this file. Pass the raw relative path
  // through (the Artifacts tab joins it onto the card workspace path); harmless
  // when the flag is OFF (the footer never emits it and consumption is gated).
  const art = search?.artifact;
  if (typeof art === "string" && art.length > 0) {
    out.artifact = art;
  }
  return out;
}

export const Route = createFileRoute("/_authenticated/board/\$boardId/card/\$cardId")({
  validateSearch: validateCardSearch,
}).lazy(() => import("./\$cardId.lazy").then((d) => d.Route));
